import { observer } from 'mobx-react-lite'
import { Box, ListItemIcon, MenuItem, Pagination, Typography } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppBaseLinkKind } from 'src/component/common/analog/molecule/link/AppBaseLinkKind'
import { useAuthStore } from 'src/feature/auth/store/auth.store'
import { OrganisationRole } from 'src/feature/control-panel/model/OrganisationRole'
import { useMyTeamStore } from '../../store/my-team.store'
import { GoBackButtonWithArrow } from 'src/component/common/original/molecule/GoBackButtonWithArrow'
import { Word } from 'src/feature/localization/component/atom/Word'
import { Title1 } from 'src/feature/localization/component/atom/Title1'
import { InsightMetrics } from './InsightMetrics'
import { ColorTokens } from 'src/feature/theme/model/Palette'
import { dayJsInstance } from 'src/feature/localization/model/Time'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { TeamRolesTable } from './TeamRolesTable'
import { SkillsSearchBar } from './SkillsSearchBar'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { DownloadReport } from './DownloadReport'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import CheckIcon from '@mui/icons-material/Check'
import { AppMenu } from 'src/component/common/analog/molecule/list/AppMenu'
import { RoleOrganisationOverviewOrder, RoleOrganisationOverviewSortBy } from '../../model/my-team-roles'
import { OrgLevelModal } from 'src/feature/control-panel/component/OrgLevelModal'

const pageSize = 6

const defaultMetricIconProps = {
  iconColor: ColorTokens.primary500,
  iconBackground: ColorTokens.primary100,
}
type FilterOption<T> = { value: T; label: string }

export type TeamRolesListProps = {
  levelId: string
  levelName: string
}

export const TeamRolesList = observer(({ levelId, levelName }: TeamRolesListProps) => {
  const { translation } = useLocalizationStore()
  const myTeamStore = useMyTeamStore()
  const authStore = useAuthStore()
  const { isLoading } = myTeamStore.getGenericFieldsState('getTeamRolesOrganisationOverview')
  const { isLoading: isRoleMetricsLoading } = myTeamStore.getGenericFieldsState('fetchTeamRolesSkillGapMetricsBatch')

  const translations = translation.pages.myTeam
  const { rolesTable } = translations
  const teamOverviewMetricsTranslations = translations.teamOverviewMetrics
  const { userFilter } = translation.pages.controlpanelOrganisation
  const [skip, setSkip] = useState(0)
  const [searchText, setSearchText] = useState('')
  const [showDownloadReport, setShowDownloadReport] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [isCreateRoleDialogOpen, setIsCreateRoleDialogOpen] = useState(false)
  const [sortBy, setSortBy] = useState<RoleOrganisationOverviewSortBy>('alphabetical')
  const [order, setOrder] = useState<RoleOrganisationOverviewOrder>('a-z')
  const menuItemRef = useRef<HTMLButtonElement | null>(null)

  const sortOptions: FilterOption<RoleOrganisationOverviewSortBy>[] = [
    { value: 'alphabetical', label: userFilter.sortAlphabetical() },
    { value: 'date-created', label: userFilter.sortDateCreated() },
  ]

  const orderOptions: FilterOption<RoleOrganisationOverviewOrder>[] = sortBy === 'alphabetical'
    ? [
      { value: 'a-z', label: userFilter.orderAlphabeticalAZ() },
      { value: 'z-a', label: userFilter.orderAlphabeticalZA() },
    ]
    : [
      { value: 'newest-first', label: userFilter.orderNewestFirst() },
      { value: 'oldest-first', label: userFilter.orderOldestFirst() },
    ]

  const selectedSortLabel = sortOptions.find(option => option.value === sortBy)?.label || sortOptions[0].label
  const selectedOrderLabel = orderOptions.find(option => option.value === order)?.label || orderOptions[0].label
  const menuLabel = `${selectedSortLabel} - ${selectedOrderLabel}`

  const loadRoleOverview = useCallback(async () => {
    await myTeamStore.getTeamRolesOrganisationOverview(levelId, skip, pageSize, searchText, sortBy, order)

    const reloadedRoleIds = myTeamStore.teamRolesOrganisationOverview.roles.map(role => role.id)

    if (reloadedRoleIds.length > 0) {
      await myTeamStore.fetchTeamRolesSkillGapMetricsBatch(reloadedRoleIds)
    }
  }, [levelId, myTeamStore, order, searchText, skip, sortBy])

  useEffect(() => {
    setSkip(0)
  }, [levelId])

  useEffect(() => {
    loadRoleOverview()
  }, [loadRoleOverview])

  const roleOverview = myTeamStore.teamRolesOrganisationOverview
  const roles = roleOverview.roles
  const isBusy = isLoading || isRoleMetricsLoading
  const hasRoles = roles.length > 0
  const shouldShowEmptyState = !isBusy && !hasRoles
  const shouldShowRolesTable = hasRoles
  const filteredRolesCount = roleOverview.filteredRoles ?? roleOverview.totalRoles ?? roles.length
  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredRolesCount / pageSize)), [filteredRolesCount])
  const currentPage = Math.floor(skip / pageSize) + 1
  const visibleStart = hasRoles ? skip + 1 : 0
  const visibleEnd = hasRoles ? Math.min(skip + roles.length, filteredRolesCount) : 0
  const resultsSummary = translations.resultsSummary(visibleStart, visibleEnd, filteredRolesCount)

  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, page: number) => {
    setSkip((page - 1) * pageSize)
  }, [])

  const handleSearchChange = useCallback((searchValue: string) => {
    setSearchText(searchValue)
    setSkip(0)
  }, [])

  const handleToggleMenu = useCallback(() => {
    setShowMenu(previous => !previous)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setShowMenu(false)
  }, [])

  const handleSortByChange = useCallback((option: RoleOrganisationOverviewSortBy) => {
    setSortBy(option)
    setOrder(option === 'alphabetical' ? 'a-z' : 'newest-first')
    setSkip(0)
    setShowMenu(false)
  }, [])

  const handleOrderChange = useCallback((option: RoleOrganisationOverviewOrder) => {
    setOrder(option)
    setSkip(0)
    setShowMenu(false)
  }, [])

  const handleOpenCreateRoleDialog = useCallback(() => {
    setIsCreateRoleDialogOpen(true)
  }, [])

  const handleCloseCreateRoleDialog = useCallback(async (reload: boolean) => {
    setIsCreateRoleDialogOpen(false)

    if (!reload) {
      return
    }

    await loadRoleOverview()
  }, [loadRoleOverview])

  const handleRoleDataChanged = useCallback(async () => {
    await loadRoleOverview()
  }, [loadRoleOverview])

  const formatHighestRatedSubtitle = (avgSkillRating?: number) => (
    typeof avgSkillRating === 'number'
      ? teamOverviewMetricsTranslations.skillRatingSubtitle(avgSkillRating)
      : teamOverviewMetricsTranslations.noRatingData()
  )

  const formatHighestGapSubtitle = (avgGap?: number, lastUpdatedAt?: string | null) => {
    if (typeof avgGap !== 'number') {
      return teamOverviewMetricsTranslations.noGapData()
    }

    if (lastUpdatedAt) {
      return teamOverviewMetricsTranslations.skillGapReviewedSubtitle(avgGap, dayJsInstance(lastUpdatedAt).fromNow())
    }

    return teamOverviewMetricsTranslations.skillGapSubtitle(avgGap)
  }
  
  return (
    <Box sx={styles.cardWrapper}>
      <AppBox alignItems='baseline' gap={5}>
        <GoBackButtonWithArrow textValue={translations.metricsHeader.team()} />
        <Word sx={{ color: 'text.secondary', fontSize: 12 }}>{` . ${levelName}`}</Word>
      </AppBox>
      <Title1 m={0} textValue={translations.rolesPageTitle} />
      <Typography variant='body2' sx={styles.subtitle}>
        {translations.rolesPageSubtitle()}
      </Typography>

      <InsightMetrics
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(auto-fill, minmax(220px, 1fr))' },
          gap: 20,
        }}
        metrics={[
          {
            id: 'total-roles',
            label: translations.metricsHeader.totalRoles(),
            value: roleOverview.totalRoles ?? 0,
            icon: 'PeopleRoundedIcon',
            ...defaultMetricIconProps,
          },
          {
            id: 'total-skills',
            label: translations.metricsHeader.totalSkills(),
            value: roleOverview.totalSkills ?? 0,
            icon: 'ExtensionRoundedIcon',
            ...defaultMetricIconProps,
          },
          {
            id: 'highest-rated-role',
            label: translations.metricsHeader.highestRatedRoles(),
            value: roleOverview.highestAvgRatedRole?.name || '-',
            subtitle: formatHighestRatedSubtitle(roleOverview.highestAvgRatedRole?.avgSkillRating),
            toolTipDesc: translations.metricsHeader.highestRatedRolesTooltip(),
            icon: 'TrendingUpRoundedIcon',
            ...defaultMetricIconProps,
          },
          {
            id: 'highest-gap-role',
            label: translations.metricsHeader.highestSkillGapRoles(),
            value: roleOverview.highestGapRole?.name || '-',
            subtitle: formatHighestGapSubtitle(roleOverview.highestGapRole?.avgGap, roleOverview.highestGapRole?.lastUpdatedAt),
            toolTipDesc: translations.metricsHeader.highestSkillGapRolesTooltip(),
            icon: 'ArrowDownwardRoundedIcon',
            iconColor: ColorTokens.red200,
            iconBackground: ColorTokens.red10,
          },
        ]}
        isLoading={isLoading}
      />

      <AppPaper sx={styles.rolesPaper}>
        <AppBox sx={styles.searchBoxContainer}>
          <AppBox sx={styles.searchBox}>
            <SkillsSearchBar
              count={roleOverview.filteredRoles ?? 0}
              value={searchText}
              onDebouncedChange={handleSearchChange}
              debounceDelay={600}
            />
          </AppBox>
          <AppBox sx={styles.actionsRow}>
            <AppBox
              component='button'
              type='button'
              sx={styles.sortTrigger}
              ref={menuItemRef}
              onClick={handleToggleMenu}
              aria-controls={showMenu ? 'roles-sort-menu' : undefined}
              aria-expanded={showMenu ? 'true' : undefined}
              aria-haspopup='menu'
            >
              {menuLabel}
              {showMenu ? <KeyboardArrowUpIcon fontSize="small" sx={{ color: 'inherit' }} /> : <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'inherit' }} />}
            </AppBox>
            <AppMenu
              anchorEl={menuItemRef.current}
              id="roles-sort-menu"
              open={showMenu}
              onClose={handleCloseMenu}
            >
              <Typography sx={styles.menuSectionTitle}>{userFilter.sortBy()}</Typography>
              {sortOptions.map(option => (
                <MenuItem key={option.value} onClick={() => handleSortByChange(option.value)} sx={styles.menuItem}>
                  <ListItemIcon>{sortBy === option.value && <CheckIcon fontSize="small" sx={{ color: 'text.primary' }} />}</ListItemIcon>
                  {option.label}
                </MenuItem>
              ))}
              <Typography sx={styles.menuSectionTitle}>{userFilter.order()}</Typography>
              {orderOptions.map(option => (
                <MenuItem key={option.value} onClick={() => handleOrderChange(option.value)} sx={styles.menuItem}>
                  <ListItemIcon>{order === option.value && <CheckIcon fontSize="small" sx={{ color: 'text.primary' }} />}</ListItemIcon>
                  {option.label}
                </MenuItem>
              ))}
            </AppMenu>
            <AppButton
              variant='light'
              fontSize={12}
              onClick={() => setShowDownloadReport(true)}
              sx={{ height: 'stretch' }}
            >
              {translations.downloadReport()}
            </AppButton>
          </AppBox>
        </AppBox>

        {shouldShowEmptyState && (
          <Box sx={styles.emptyStateArea}>
            <Box sx={styles.emptyStateContainer}>
              <Typography sx={styles.emptyStateText}>
                {translations.noRolesMsg()}{' '}
                {authStore.hasRole(OrganisationRole.SystemAdministrator) && (
                  <AppBaseLinkKind kind='buttonLikeLink' onClick={handleOpenCreateRoleDialog} sx={styles.emptyStateLink}>
                    <Word sx={styles.emptyStateLinkText}>{rolesTable.rolesTodoAction()}</Word>
                  </AppBaseLinkKind>
                )}
              </Typography>
            </Box>
          </Box>
        )}

        {shouldShowRolesTable && (
          <Box sx={styles.tableArea}>
            <TeamRolesTable roles={roles} isLoading={isBusy} onRoleDataChanged={handleRoleDataChanged} />
          </Box>
        )}

        <AppBox sx={styles.paginationRow}>
          <Typography sx={styles.paginationSummary}>
            {resultsSummary}
          </Typography>
          <Pagination
            variant='outlined'
            shape='rounded'
            color='secondary'
            count={totalPages}
            page={currentPage}
            showFirstButton
            showLastButton
            onChange={handlePageChange}
          />
        </AppBox>
      </AppPaper>

      {showDownloadReport && (
        <DownloadReport
          show={showDownloadReport}
          levelId={levelId}
          onClose={() => setShowDownloadReport(false)}
        />
      )}
      {isCreateRoleDialogOpen && (
        <OrgLevelModal
          show={isCreateRoleDialogOpen}
          onClose={handleCloseCreateRoleDialog}
          editLevel={null}
          parentId={levelId}
          lockedCreateType='role'
        />
      )}
    </Box>
  )
})

const styles = {
  cardWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    minHeight: 'calc(100vh - 100px)',
  },
  subtitle: {
    color: 'text.secondary',
    marginTop: '-15px',
  },
  paginationRow: {
    width: '100%',
    flexShrink: 0,
    display: 'flex',
    justifyContent: { xs: 'center', md: 'space-between' },
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    backgroundColor: 'background.dark',
    padding: '10px 20px',
    border: '1px solid',
    borderColor: 'background.default',
    borderRadius: '10px',
  },
  paginationSummary: {
    color: 'text.secondary',
    fontSize: 12,
    width: { xs: '100%', md: 'auto' },
    textAlign: { xs: 'center', md: 'left' },
  },
  rolesPaper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '10px',
    padding: '10px 15px',
    gap: 20,
    border: 0,
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  tableArea: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  emptyStateArea: {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    placeItems: 'center',
    width: '100%',
  },
  emptyStateContainer: {
    width: '100%',
    display: 'grid',
    placeItems: 'center',
    textAlign: 'center',
    padding: '24px 16px',
  },
  emptyStateText: {
    display: 'inline-block',
    maxWidth: '44rem',
    margin: 0,
    color: 'text.secondary',
    fontSize: 12,
    lineHeight: 1.5,
    textAlign: 'center',
  },
  searchBoxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    width: '100%',
    flexShrink: 0,
  },
  searchBox: {
    width: { xs: '100%', md: '26em' },
    maxWidth: '100%',
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  sortTrigger: {
    display: 'flex',
    flexDirection: 'row',
    gap: 1,
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: 12,
    backgroundColor: 'primary.main',
    color: 'background.default',
    p: '5px 10px',
    borderRadius: '10px',
  },
  menuSectionTitle: {
    color: 'text.secondary',
    fontSize: 11,
    p: '4px 10px',
  },
  menuItem: {
    fontSize: 12,
    '& .MuiListItemIcon-root': {
      minWidth: 24,
    },
  },
  emptyStateLink: {
    fontSize: 14,
    lineHeight: 1.4,
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'baseline',
  },
  emptyStateLinkText: {
    color: ColorTokens.primary500,
    fontSize: 'inherit',
    lineHeight: 'inherit',
    fontWeight: 'inherit',
  },
}
